import {
  CtaPanelComponent,
  EmailSentComponentModule,
  LockedAccountApiService,
} from '@accounts/feature-auth';
import { MiniFooterComponent } from '@accounts/ui-common';
import { AccredibleButtonLoadingComponentModule } from '@accredible-frontend-v2/components/button-loading';
import { AccredibleDividerModule } from '@accredible-frontend-v2/components/divider';
import { environment } from '@accredible-frontend-v2/envs';
import { AccredibleTextInputComponentModule } from '@accredible-frontend-v2/forms/text-input';
import { AccredibleBaseFormComponent } from '@accredible-frontend-v2/forms/utils';
import { emailValidator } from '@accredible-frontend-v2/forms/validators';
import { AccredibleBruteForceErrorStatus } from '@accredible-frontend-v2/models';
import { AccredibleIconComponentModule } from '@accredible-frontend-v2/new-components/icon';
import { NavigationBackComponent } from '@accredible-frontend-v2/new-components/navigation-back';
import { TopBorderCardComponent } from '@accredible-frontend-v2/new-components/top-border-card';
import { TwoColumnsLayoutComponent } from '@accredible-frontend-v2/new-components/two-columns-layout';
import { AccredibleRecaptchaModule } from '@accredible-frontend-v2/recaptcha';
import { AccredibleAuthenticationApiService } from '@accredible-frontend-v2/services/authentication';
import { AccredibleLanguageService } from '@accredible-frontend-v2/services/language';
import { AccredibleToastService } from '@accredible-frontend-v2/toast';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { CommonModule, DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { finalize } from 'rxjs/operators';
import { AccredibleLoginService } from '../../shared/services/login/acs-login.service';
import { RetrieveCredentialsApiService } from '../retrieve-credentials/service/retrieve-credentials.service';

const MATERIAL_MODULES = [MatButtonModule];

const TRANSLATION_KEY = 'login';

export enum TABS {
  LOGIN = 'login',
  RETRIEVE_CREDENTIALS = 'retrieve-credentials',
}

@Component({
  templateUrl: './login.container.html',
  styleUrls: [`./login.container.scss`],
  standalone: true,
  imports: [
    CtaPanelComponent,
    TwoColumnsLayoutComponent,
    TopBorderCardComponent,
    MiniFooterComponent,
    NavigationBackComponent,
    CommonModule,
    ReactiveFormsModule,
    EmailSentComponentModule,
    AccredibleButtonLoadingComponentModule,
    AccredibleTextInputComponentModule,
    AccredibleRecaptchaModule,
    TranslocoModule,
    AccredibleDividerModule,
    AccredibleIconComponentModule,
    RouterModule,
    ...MATERIAL_MODULES,
  ],
})
export class LoginContainer
  extends AccredibleBaseFormComponent<{
    email: string;
    password: string;
  }>
  implements OnInit
{
  protected readonly _el = inject(ElementRef<HTMLElement>);

  private readonly _fb = inject(UntypedFormBuilder);
  private readonly _lockedAccountApi = inject(LockedAccountApiService);
  private readonly _authenticationApi = inject(AccredibleAuthenticationApiService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _toast = inject(AccredibleToastService);
  private readonly _title = inject(Title);
  private readonly _login = inject(AccredibleLoginService);
  private readonly _language = inject(AccredibleLanguageService);
  private readonly _document = inject(DOCUMENT);
  private readonly _retrieveCredentialsApi = inject(RetrieveCredentialsApiService);
  private readonly _liveAnnouncer = inject(LiveAnnouncer);

  isLoading = signal(false);
  isEmailSent = signal(false);

  showRetrieveCredentials = signal(false);

  constructor() {
    super(inject(ElementRef<HTMLElement>));

    this.formGroup = this._fb.group({
      email: [null, { validators: [Validators.required, emailValidator()], updateOn: 'blur' }],
      password: [null, Validators.required],
    });

    this._title.setTitle('Accredible • Accounts • Login');

    const currentUrl = this._router.url;
    if (currentUrl.includes(TABS.RETRIEVE_CREDENTIALS)) {
      this.selectTab(TABS.RETRIEVE_CREDENTIALS, false);
    } else {
      this.selectTab(TABS.LOGIN, false);
    }
  }

  ngOnInit(): void {
    this._router.events.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this._updateTabBasedOnRoute();
    });
  }

  private _updateTabBasedOnRoute(): void {
    const currentUrl = this._router.url;
    if (currentUrl.includes(TABS.RETRIEVE_CREDENTIALS)) {
      this.selectTab(TABS.RETRIEVE_CREDENTIALS, false);
    } else if (currentUrl.includes(TABS.LOGIN)) {
      this.selectTab(TABS.LOGIN, false);
    }
  }

  selectTab(tab: TABS, shouldNavigate: boolean = true): void {
    this.showRetrieveCredentials.set(tab === TABS.RETRIEVE_CREDENTIALS);

    const passwordControl = this.formGroup.get('password');

    if (tab === TABS.RETRIEVE_CREDENTIALS) {
      passwordControl.removeValidators(Validators.required);
    } else {
      passwordControl.addValidators(Validators.required);
    }

    this.formGroup.reset();

    if (shouldNavigate) {
      const targetRoute = tab;

      if (targetRoute && !this._router.url.includes(targetRoute)) {
        this._router.navigate([targetRoute]);
      }
    }
  }

  login(): void {
    if (!this._isFormValid()) {
      return;
    }

    this._toast.dismissCurrentToast();
    this.isLoading.set(true);

    this._authenticationApi
      .login({ ...this.formGroup.getRawValue(), origin: this._getOrigin() })
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe({
        next: (res) => this._login.loginRedirectionHandler(res),
        error: (error) => this._loginError(error),
      });
  }

  retrieveCredentials(): void {
    if (this._isFormValid()) {
      this.isLoading.set(true);
      this._retrieveCredentialsApi
        .requestRetrieveCredentials(this.formGroup.getRawValue())
        .pipe(
          finalize(() => {
            this.isLoading.set(false);
          }),
        )
        .subscribe({
          next: () => {
            this.isEmailSent.set(true);

            this._liveAnnouncer.announce(
              this._language.translate(
                `${TRANSLATION_KEY}.retrieve-credentials-form.email_sent.title`,
              ),
              'polite',
              1500,
            );
          },
          error: () => {
            this._toast.error(
              this._language.translate(
                `${TRANSLATION_KEY}.retrieve-credentials-form.error_message`,
              ),
            );
          },
        });
    }
  }

  private _loginError(httpError: HttpErrorResponse): void {
    const passwordControl = this.formGroup.get('password');

    // Login failed
    switch (httpError.error.status) {
      case AccredibleBruteForceErrorStatus.NOT_VERIFIED:
        this._toast.error(
          this._language.translate(`${TRANSLATION_KEY}.sign-in-form.user_not_verified`),
        );
        break;

      case AccredibleBruteForceErrorStatus.INVALID_CREDENTIALS:
        passwordControl.setErrors({ invalidRequest: true });
        break;

      case AccredibleBruteForceErrorStatus.INVALID_CREDENTIALS_THIRD_ATTEMPT:
        passwordControl.setErrors({ invalidThirdRequest: true });
        break;

      case AccredibleBruteForceErrorStatus.INVALID_CREDENTIALS_FOURTH_ATTEMPT:
        passwordControl.setErrors({ invalidFourthRequest: true });
        break;

      case AccredibleBruteForceErrorStatus.ACCOUNT_LOCKED:
        this._lockedAccountApi.setLockedUserEmail(this.formGroup.get('email').value);
        this._router.navigate(['locked-account'], { queryParams: { origin: this._getOrigin() } });
        break;
    }

    this._focusFirstInvalidInput();
  }

  private _getOrigin(): string {
    let origin = new URL(environment.credentialNetUrl).hostname;

    if (this._route.snapshot.queryParams.origin) {
      const url = new URL(this._route.snapshot.queryParams.origin);
      origin = url.hostname;
    }

    return origin;
  }
}
